import { Column, Row } from 'src/shared/components/Semantic/all'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { cn } from 'src/shared/components/ui/utils'

/** A placeholder block with a light sweep across it. */
const Bone = ({ className }: { className?: string }) => (
  <div
    className={cn(
      'relative overflow-hidden rounded-md bg-proposal-placeholder',
      className
    )}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent motion-reduce:hidden" />
  </div>
)

const cardClass =
  'flex flex-col gap-3.5 rounded-[20px] border border-[var(--raised-border)] bg-proposal-surface shadow-soft p-6'

const LineSkeleton = () => (
  <section className={cardClass}>
    <Row className="items-baseline gap-2.5">
      <Bone className="h-6 w-28" />
      <Bone className="h-4 w-24" />
      <Bone className="ml-auto h-5 w-20" />
    </Row>
    <div className="flex flex-wrap gap-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="flex flex-[1_1_11rem] flex-col gap-2.5 rounded-[14px] border border-proposal-ink/10 bg-proposal-option p-3.5"
        >
          <Bone className="aspect-[4/3] w-full rounded-[10px]" />
          <Bone className="h-4 w-3/4" />
          <Bone className="h-3 w-full" />
          <Bone className="h-3 w-1/3" />
        </div>
      ))}
    </div>
    <Column className="gap-2 border-t border-proposal-ink/[0.09] pt-3">
      <Bone className="h-3.5 w-full" />
      <Bone className="h-3.5 w-full" />
      <Bone className="h-3.5 w-2/3" />
    </Column>
  </section>
)

/** Loading skeleton in the shape of the proposal page (Design Surface · 03). */
export const LoadingState = () => {
  const { t } = useLocalization()

  return (
    <div
      className="proposal-page min-h-dvh w-full overflow-x-hidden bg-proposal-ground font-tiktok-sans font-extralight text-proposal-ink"
      aria-busy="true"
    >
      <span className="sr-only" role="status">
        {t('Opening the proposal', 'Öppnar förslaget')}
      </span>

      <header className="flex items-center gap-4 border-b border-proposal-ink/[0.09] px-[5%] py-5">
        <span className="text-lg font-semibold tracking-[-0.02em]">
          Klayder
        </span>
        <div className="ml-8 hidden gap-6 md:flex">
          <Bone className="h-3.5 w-16" />
          <Bone className="h-3.5 w-20" />
          <Bone className="h-3.5 w-20" />
        </div>
        <Bone className="ml-auto h-11 w-36 rounded-full" />
      </header>

      <div
        className="mx-auto box-content flex max-w-[1080px] flex-wrap items-start gap-9 px-[5%] pb-16 pt-8 sm:pt-11"
        aria-hidden="true"
      >
        <main className="flex min-w-0 flex-[999_1_34rem] flex-col gap-[26px]">
          <Column className="gap-2.5">
            <Row className="items-center gap-3">
              <Bone className="h-4 w-40" />
              <Bone className="h-7 w-36 rounded-full" />
            </Row>
            <Bone className="h-12 w-4/5 rounded-lg" />
            <Bone className="h-12 w-1/2 rounded-lg" />
            <Bone className="mt-1 h-4 w-full" />
            <Bone className="h-4 w-2/3" />
            <Row className="mt-1 gap-4">
              <Bone className="h-3.5 w-24" />
              <Bone className="h-3.5 w-28" />
              <Bone className="h-3.5 w-40" />
            </Row>
          </Column>
          <LineSkeleton />
          <LineSkeleton />
        </main>

        <aside className="box-content flex min-w-0 flex-[1_1_17rem] flex-col gap-4 rounded-[20px] border border-[var(--raised-border)] bg-proposal-surface shadow-soft p-6 lg:sticky lg:top-6">
          <Bone className="h-3.5 w-24" />
          <Column className="gap-3">
            {[0, 1, 2, 3].map((i) => (
              <Row key={i} className="justify-between">
                <Bone className="h-3.5 w-20" />
                <Bone className="h-3.5 w-16" />
              </Row>
            ))}
          </Column>
          <Row className="items-baseline justify-between border-t border-proposal-ink/[0.09] pt-4">
            <Bone className="h-4 w-24" />
            <Bone className="h-7 w-28" />
          </Row>
          <Bone className="h-[52px] w-full rounded-[13px]" />
          <Bone className="h-3 w-full" />
          <Bone className="h-3 w-3/4" />
        </aside>
      </div>
    </div>
  )
}
